import type { NextApiRequest, NextApiResponse } from 'next';
import { withAuth } from '../../../lib/apiAuth';
import { getSupabaseAdminClient } from '../../../lib/supabaseAdmin';

/**
 * S5: Tax & Compliance Dashboard API
 * Returns deterministic tax data from reporting tables (never from Cegid directly).
 * Supports period filtering, comparison, and anomaly detection.
 * 
 * GET /api/dashboard/tax-compliance?clienteId=X&periodo=2026-01&comparar=true
 */
async function handler(
  req: NextApiRequest,
  res: NextApiResponse,
  user: { id: string; email: string; roles: string[]; primaryRole: string }
) {
  if (req.method !== 'GET') {
    return res.status(405).json({ error: 'Method not allowed' });
  }

  try {
    const supabaseAdmin = getSupabaseAdminClient();
    const { clienteId, periodo, ejercicio, comparar } = req.query;

    // Build query for tax_summary
    let query = supabaseAdmin.from('tax_summary').select('*');

    if (clienteId) {
      query = query.eq('cliente_id', clienteId as string);
    }

    if (periodo) {
      query = query.eq('periodo', periodo as string);
    }

    if (ejercicio) {
      query = query.eq('ejercicio_fiscal', ejercicio as string);
    }

    query = query.order('periodo', { ascending: false });

    const { data: taxSummaries, error } = await query;
    if (error) throw error;

    // Get IVA detail breakdown for each summary
    const summariesWithDetail = [];
    for (const summary of taxSummaries || []) {
      const { data: ivaDetails } = await supabaseAdmin
        .from('tax_iva_detail')
        .select('*')
        .eq('tax_summary_id', summary.id)
        .order('porcentaje_iva', { ascending: false });

      summariesWithDetail.push({
        ...summary,
        iva_detail: ivaDetails || [],
      });
    }

    // Period comparison (current vs previous)
    let comparison = null;
    if (comparar === 'true' && periodo && clienteId) {
      const [year, month] = (periodo as string).split('-').map(Number);
      const prevMonth = month === 1 ? 12 : month - 1;
      const prevYear = month === 1 ? year - 1 : year;
      const prevPeriodo = `${prevYear}-${String(prevMonth).padStart(2, '0')}`;

      const { data: prevSummary } = await supabaseAdmin
        .from('tax_summary')
        .select('*')
        .eq('cliente_id', clienteId as string)
        .eq('periodo', prevPeriodo)
        .maybeSingle();

      const { data: currentSummary } = await supabaseAdmin
        .from('tax_summary')
        .select('*')
        .eq('cliente_id', clienteId as string)
        .eq('periodo', periodo as string)
        .maybeSingle();

      if (prevSummary && currentSummary) {
        comparison = {
          periodo_actual: periodo,
          periodo_anterior: prevPeriodo,
          variaciones: {
            iva_repercutido: calcVariacion(currentSummary.iva_repercutido, prevSummary.iva_repercutido),
            iva_soportado: calcVariacion(currentSummary.iva_soportado, prevSummary.iva_soportado),
            base_imponible_emitidas: calcVariacion(currentSummary.base_imponible_emitidas, prevSummary.base_imponible_emitidas),
            base_imponible_recibidas: calcVariacion(currentSummary.base_imponible_recibidas, prevSummary.base_imponible_recibidas),
            retenciones_practicadas: calcVariacion(currentSummary.retenciones_practicadas, prevSummary.retenciones_practicadas),
            num_facturas_emitidas: calcVariacion(currentSummary.num_facturas_emitidas, prevSummary.num_facturas_emitidas),
            num_facturas_recibidas: calcVariacion(currentSummary.num_facturas_recibidas, prevSummary.num_facturas_recibidas),
          },
          anterior: prevSummary,
        };
      }
    }

    // Anomaly detection
    const anomalies = detectAnomalies(summariesWithDetail);

    // Aggregate annual totals if ejercicio provided
    let annualTotals = null;
    if (ejercicio && clienteId) {
      const annualSummaries = (taxSummaries || []).filter(
        s => s.ejercicio_fiscal === ejercicio
      );
      annualTotals = {
        ejercicio,
        total_iva_repercutido: sum(annualSummaries, 'iva_repercutido'),
        total_iva_soportado: sum(annualSummaries, 'iva_soportado'),
        total_iva_resultado: sum(annualSummaries, 'iva_resultado'),
        total_base_emitidas: sum(annualSummaries, 'base_imponible_emitidas'),
        total_base_recibidas: sum(annualSummaries, 'base_imponible_recibidas'),
        total_retenciones_practicadas: sum(annualSummaries, 'retenciones_practicadas'),
        total_retenciones_soportadas: sum(annualSummaries, 'retenciones_soportadas'),
        total_suplidos: sum(annualSummaries, 'total_suplidos'),
        total_facturas_emitidas: sum(annualSummaries, 'num_facturas_emitidas'),
        total_facturas_recibidas: sum(annualSummaries, 'num_facturas_recibidas'),
        meses_con_datos: annualSummaries.length,
      };
    }

    return res.status(200).json({
      success: true,
      data: {
        summaries: summariesWithDetail,
        comparison,
        anomalies,
        annual_totals: annualTotals,
      },
    });
  } catch (error: any) {
    console.error('[Tax Dashboard] Error:', error);
    return res.status(500).json({ error: 'Error al obtener datos fiscales.' });
  }
}

function calcVariacion(actual: number, anterior: number): {
  actual: number;
  anterior: number;
  diferencia: number;
  porcentaje: number;
  anomalia: boolean;
} {
  const a = Number(actual) || 0;
  const b = Number(anterior) || 0;
  const diff = a - b;
  const pct = b !== 0 ? ((diff / Math.abs(b)) * 100) : (a !== 0 ? 100 : 0);
  return {
    actual: a,
    anterior: b,
    diferencia: diff,
    porcentaje: Math.round(pct * 100) / 100,
    anomalia: Math.abs(pct) > 20, // >20% deviation = anomaly
  };
}

function sum(items: any[], field: string): number {
  return items.reduce((acc, item) => acc + (Number(item[field]) || 0), 0);
}

interface Anomaly {
  tipo: string;
  severidad: 'info' | 'warning' | 'danger';
  periodo: string;
  mensaje: string;
  valor: number;
}

function detectAnomalies(summaries: any[]): Anomaly[] {
  const anomalies: Anomaly[] = [];

  for (let i = 0; i < summaries.length; i++) {
    const current = summaries[i];
    const prev = summaries[i + 1]; // Sorted desc, so [i+1] is the previous period

    if (!prev) continue;

    // IVA repercutido drop >30%
    const ivaRepPrev = Number(prev.iva_repercutido) || 0;
    const ivaRepCurr = Number(current.iva_repercutido) || 0;
    if (ivaRepPrev > 0 && ivaRepCurr < ivaRepPrev * 0.7) {
      anomalies.push({
        tipo: 'iva_repercutido_drop',
        severidad: 'warning',
        periodo: current.periodo,
        mensaje: `IVA repercutido cayó un ${Math.round(((ivaRepPrev - ivaRepCurr) / ivaRepPrev) * 100)}% respecto al período anterior`,
        valor: ivaRepCurr,
      });
    }

    // Invoice count drop >50%
    const factPrev = Number(prev.num_facturas_emitidas) || 0;
    const factCurr = Number(current.num_facturas_emitidas) || 0;
    if (factPrev > 0 && factCurr < factPrev * 0.5) {
      anomalies.push({
        tipo: 'facturas_emitidas_drop',
        severidad: 'danger',
        periodo: current.periodo,
        mensaje: `Número de facturas emitidas cayó un ${Math.round(((factPrev - factCurr) / factPrev) * 100)}%`,
        valor: factCurr,
      });
    }

    // IVA resultado negative (paying more VAT than collecting)
    const ivaResultado = Number(current.iva_resultado) || 0;
    if (ivaResultado < -1000) {
      anomalies.push({
        tipo: 'iva_resultado_negative',
        severidad: 'info',
        periodo: current.periodo,
        mensaje: `IVA a compensar: ${Math.abs(ivaResultado).toFixed(2)}€ (soportado > repercutido)`,
        valor: ivaResultado,
      });
    }

    // Zero invoices in a period (potential missing data)
    if (factCurr === 0 && (Number(current.num_facturas_recibidas) || 0) === 0) {
      anomalies.push({
        tipo: 'missing_data',
        severidad: 'warning',
        periodo: current.periodo,
        mensaje: 'No hay facturas registradas en este período. ¿Faltan datos de sincronización?',
        valor: 0,
      });
    }
  }

  return anomalies;
}

export default withAuth(handler, ['admin', 'asesor']);
